import React from "react";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
} from "@heroicons/react/20/solid";
import { cn } from "../utils/style";

export const StatusColor = {
  // Standard Statuses
  default: "bg-slate-50 text-slate-500 border-slate-200/60",
  pending:
    "bg-amber-50 text-amber-600 border-amber-200/50 shadow-sm shadow-amber-500/5",
  completed:
    "bg-emerald-50 text-emerald-600 border-emerald-200/50 shadow-sm shadow-emerald-500/5",
  paid: "bg-emerald-50 text-emerald-600 border-emerald-200/50 shadow-sm shadow-emerald-500/5",
  success:
    "bg-emerald-50 text-emerald-600 border-emerald-200/50 shadow-sm shadow-emerald-500/5",

  // Info/Action Statuses
  alreadyexists:
    "bg-blue-50 text-blue-600 border-blue-200/50 shadow-sm shadow-blue-500/5",
  already_exists:
    "bg-blue-50 text-blue-600 border-blue-200/50 shadow-sm shadow-blue-500/5",
  "in progress":
    "bg-primary-50 text-primary-600 border-primary-200/50 shadow-sm shadow-primary-500/5",
  on_hold: "bg-slate-100 text-slate-600 border-slate-200/60",

  // Error/Warning Statuses
  failed: "bg-red-50 text-red-600 border-red-200/50 shadow-sm shadow-red-500/5",
  canceled:
    "bg-red-50 text-red-600 border-red-200/50 shadow-sm shadow-red-500/5",
  "cancel member":
    "bg-red-50 text-red-600 border-red-200/50 shadow-sm shadow-red-500/5",
  "cancel policy":
    "bg-red-50 text-red-600 border-red-200/50 shadow-sm shadow-red-500/5",
  rejected:
    "bg-red-50 text-red-600 border-red-200/50 shadow-sm shadow-red-500/5",
  expired: "bg-slate-100 text-slate-500 border-slate-200/60",
  not_found: "bg-red-50 text-red-600 border-red-200/50",
  unknown_error: "bg-red-50 text-red-600 border-red-200/50",

  // Binary Statuses
  active: "bg-emerald-50 text-emerald-600 border-emerald-200/50",
  inactive: "bg-slate-100 text-slate-500 border-slate-200/60",
  accepted: "bg-emerald-50 text-emerald-600 border-emerald-200/50",
  yes: "bg-emerald-50 text-emerald-600 border-emerald-200/50",
  no: "bg-red-50 text-red-600 border-red-200/50",
};

function StatusBadge({ status, boolean, className }) {
  const { t } = useTranslation();

  if (boolean) {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border transition-all duration-300 shadow-sm",
          status === true &&
            "bg-emerald-50 text-emerald-600 border-emerald-200/50 shadow-emerald-500/5",
          status === false &&
            "bg-red-50 text-red-600 border-red-200/50 shadow-red-500/5",
          status === null &&
            "bg-amber-50 text-amber-600 border-amber-200/50 shadow-amber-500/5",
          className,
        )}
      >
        {status === true && <CheckCircleIcon className="h-3.5 w-3.5" />}
        {status === false && <XCircleIcon className="h-3.5 w-3.5" />}
        {status === null && <ExclamationCircleIcon className="h-3.5 w-3.5" />}
        <span>
          {status === true ? t("Yes") : status === false ? t("No") : t("N/A")}
        </span>
      </div>
    );
  }

  const lowerStatus = status?.toLowerCase() || "default";
  const statusClass = StatusColor[lowerStatus] || StatusColor.default;

  // Determine icon based on status
  const getIcon = () => {
    if (
      ["completed", "paid", "success", "active", "accepted", "yes"].includes(
        lowerStatus,
      )
    )
      return <CheckCircleIcon className="h-3 w-3 me-1.5 opacity-80" />;
    if (["pending", "in progress", "on_hold"].includes(lowerStatus))
      return <ClockIcon className="h-3 w-3 me-1.5 opacity-80 animate-pulse" />;
    if (
      [
        "failed",
        "canceled",
        "rejected",
        "no",
        "not_found",
        "unknown_error",
      ].includes(lowerStatus)
    )
      return <XCircleIcon className="h-3 w-3 me-1.5 opacity-80" />;
    return null;
  };

  return (
    <span
      className={clsx(
        statusClass,
        "inline-flex items-center rounded-lg px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.15em] border transition-all duration-500 hover:scale-105 hover:shadow-md",
        className,
      )}
    >
      {getIcon()}
      {t(status)}
    </span>
  );
}

export default StatusBadge;
